const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const supported = () => typeof window !== "undefined" && window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const headers = token => ({"Content-Type": "application/json", ...(token ? {Authorization: `Bearer ${token}`} : {})});
const currentToken = () => localStorage.getItem("mobile_token");
const bytes = value => Uint8Array.from(atob((value + "=".repeat((4 - value.length % 4) % 4)).replace(/-/g, "+").replace(/_/g, "/")), c => c.charCodeAt(0));

export async function registerServiceWorker() {
  if (!supported()) return null;
  return navigator.serviceWorker.register("/sw.js", {scope: "/"});
}
const activeWorker = async () => {
  await registerServiceWorker();
  let timer;
  try { return await Promise.race([navigator.serviceWorker.ready, new Promise((_, reject) => { timer=setTimeout(() => reject(new Error("Η υπηρεσία ειδοποιήσεων δεν είναι έτοιμη.")), 10000); })]); }
  finally { clearTimeout(timer); }
};
const matchesApplicationServerKey = (current, expected) => {
  if (!current || !expected) return false;
  let currentBytes;
  if (current instanceof ArrayBuffer) currentBytes = new Uint8Array(current);
  else if (ArrayBuffer.isView(current)) currentBytes = new Uint8Array(current.buffer, current.byteOffset, current.byteLength);
  else return false;
  return currentBytes.length === expected.length && expected.every((value, index) => currentBytes[index] === value);
};
async function currentSubscription(registration, publicKey, token, existing) {
  let subscription = existing === undefined ? await registration.pushManager.getSubscription() : existing;
  if (subscription && !matchesApplicationServerKey(subscription.options?.applicationServerKey, publicKey)) {
    await unsubscribeFromPush(token);
    subscription = await registration.pushManager.getSubscription();
    if (subscription && !matchesApplicationServerKey(subscription.options?.applicationServerKey, publicKey)) {
      throw new Error("Η παλιά εγγραφή ειδοποιήσεων δεν ανανεώθηκε.");
    }
  }
  return subscription;
}
async function save(subscription, token = currentToken()) {
  const response = await fetch(`${API}/push/subscribe`, {method: "POST", headers: headers(token), body: JSON.stringify(subscription.toJSON())});
  if (!response.ok) throw new Error("Η εγγραφή της συσκευής δεν ολοκληρώθηκε.");
  return subscription;
}
export async function subscribeToPush() {
  if (!supported()) return null;
  // Must be invoked directly from the visible enable button, before asynchronous work.
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;
  const registration = await activeWorker();
  const response = await fetch(`${API}/push/vapid-key`);
  const config = await response.json();
  if (!response.ok || !config.public_key) throw new Error("Η υπηρεσία ειδοποιήσεων δεν έχει ρυθμιστεί.");
  let subscription = await currentSubscription(registration, bytes(config.public_key), currentToken());
  if (!subscription) subscription = await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: bytes(config.public_key)});
  try { const saved = await save(subscription); localStorage.setItem("lefteria_push_enabled", "1"); return saved; }
  catch (error) { await subscription.unsubscribe(); throw error; }
}
export async function syncPushSubscription(token = currentToken()) {
  if (!supported() || Notification.permission !== "granted") return;
  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) return;
  const existing = await registration.pushManager.getSubscription();
  const wantsPush = localStorage.getItem("lefteria_push_enabled") === "1";
  if (!existing && !(token && wantsPush)) return;
  const response = await fetch(API + "/push/vapid-key");
  const config = await response.json();
  if (!response.ok || !config.public_key) return;
  let subscription = await currentSubscription(registration, bytes(config.public_key), token, existing);
  if (!subscription && token && (existing || wantsPush)) {
    subscription = await registration.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: bytes(config.public_key)});
  }
  if (subscription) await save(subscription, token);
}
export async function unsubscribeFromPush(token = currentToken()) {
  if (!supported()) return;
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  // Revoke at the browser even if a disconnected server cannot remove the row yet.
  let serverError;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(`${API}/push/unsubscribe`, {method: "POST", headers: headers(token), body: JSON.stringify(subscription.toJSON()), signal: controller.signal});
    if (!response.ok) serverError = new Error("Η διαγραφή της εγγραφής απέτυχε.");
  } catch (error) { serverError = error; }
  finally { clearTimeout(timer); }
  let revoked = false;
  try { revoked = await subscription.unsubscribe(); } catch { /* Server removal is independently sufficient. */ }
  if (!revoked && serverError) throw new Error("Η συσκευή δεν αποσυνδέθηκε από τις ειδοποιήσεις. Δοκιμάστε ξανά.");
}
export async function getSubscriptionState() {
  if (!supported()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? "subscribed" : "unsubscribed";
}